import { v4 as uuid } from 'uuid';

/**
 * Inserts a package template into the current canvas.
 */
class UmlPkgLibInserter {
    /**
     * Regenerates every id (shapes, captions, members, connectors, waypoints) so the same
     * template can be inserted more than once without collisions, remaps every internal
     * reference (parentId, fromShapeId/toShapeId, connectorId), and translates the whole
     * group as a rigid block to the right of every existing top-level shape.
     *
     * Never inserts on top of an existing shape: PackageContainmentHandler's containsShape
     * is purely geometric, so landing inside another package would silently adopt the
     * pasted shapes into it, or let it steal connectors meant for the pasted package.
     *
     * Returns plain shapes/connectors, not class instances — the caller runs these through
     * UmlShapeSerializer.fromPlainShape/fromPlainConnector before handing them to
     * umlCanvasEngine/umlConnectorEngine.
     * @param {object} template - {package, satellites, connectors}, from UmlPkgLibBuilder.build
     * @param {object[]} allShapes - top-level shapes in the TARGET schema (umlCanvasEngine.shapes)
     * @returns {{shapes: object[], connectors: object[]}|null} null if a top-level package with
     *   the same name already exists in allShapes (no duplicate insert)
     */
    static insert(template, allShapes) {
        const nameExists = allShapes.some((shape) => shape.kind === 'package' && shape.name === template.package.name);
        if (nameExists) return null;

        const idMap = new Map();
        const collectIds = (shape) => {
            idMap.set(shape.id, uuid());
            if (Array.isArray(shape.children)) {
                shape.children.forEach(collectIds);
            }
        };
        collectIds(template.package);
        template.satellites.forEach(collectIds);

        const applyIds = (shape) => {
            shape.id = idMap.get(shape.id);
            shape.parentId = shape.parentId ? (idMap.get(shape.parentId) ?? null) : null;
            if (shape.caption) shape.caption.id = uuid();
            if (Array.isArray(shape.members)) {
                shape.members.forEach((member) => {
                    member.id = uuid();
                });
            }
            if (Array.isArray(shape.children)) {
                shape.children.forEach(applyIds);
            }
        };
        applyIds(template.package);
        template.satellites.forEach(applyIds);

        const connectors = template.connectors.map((connector) => {
            connector.id = uuid();
            connector.fromShapeId = idMap.get(connector.fromShapeId);
            connector.toShapeId = idMap.get(connector.toShapeId);
            connector.points.forEach((point) => {
                point.id = uuid();
                point.connectorId = connector.id;
            });
            if (connector.label) connector.label.id = uuid();
            return connector;
        });

        // Rigid-group translate — only X shifts (to the right of the rightmost existing
        // shape), Y stays as laid out in the template.
        const rightmostEdge = allShapes.reduce((max, shape) => Math.max(max, shape.x + shape.w), 0);
        const deltaX = rightmostEdge + 40 - template.package.x;
        const translate = (shape) => {
            shape.x += deltaX;
            if (shape.caption) shape.caption.x += deltaX;
            if (Array.isArray(shape.children)) shape.children.forEach(translate);
        };
        translate(template.package);
        template.satellites.forEach(translate);
        connectors.forEach((connector) => {
            connector.points.forEach((point) => {
                point.x += deltaX;
            });
            if (connector.label) connector.label.x += deltaX;
        });

        return { shapes: [template.package, ...template.satellites], connectors };
    }
}

export default UmlPkgLibInserter;
